import { IPublicClientApplication } from "@azure/msal-browser";
import { acquireToken } from "./msal";

export interface Zone {
  UniqueId: string;
  Code: string;
  OSMEdges: string;
  StreetRightSide: boolean;
  NumSpaces?: number;
  Width?: number;
  Direction: string;
  Length: number;
  AvailableSpaceLengths?: number[];
  StreetSide: string;
  Median?: boolean;
  StreetStartOffset: number;
  StreetName: string;
  CrossStreetStartName: string;
  CrossStreetEndName: string;
  Spots?: {
    Code: string;
    MeterCode?: string;
  }[];
  Color: string;
  Policies: Policy[];
}

export interface Policy {
  Id: number;
  PublishedDate: Date;
  Priority: number;
  Rules: Rule[];
  TimeSpans: TimeSpans[] | null;
}

export interface Rule {
  Activity: Activity;
  MaxStay: number | null;
  MaxStayUnit: UnitOfTime | null;
  NoReturn: number | null;
  NoReturnUnit: UnitOfTime | null;
  UserClasses: UserClass[] | null;
  Rate: Rate[] | null;
}

export interface TimeSpans {
  StartDate: Date | null;
  EndDate: Date | null;
  DaysOfWeek: DayOfWeek[] | null;
  DaysOfMonth: number[] | null;
  Months: Month[] | null;
  TimeOfDayStart: string | null;
  TimeOfDayEnd: string | null;
  DesignatedPeriod: string | null;
  DesignatedPeriodExcept: boolean | null;
}

export interface Rate {
  Rate: number;
  RateUnit: UnitOfTime;
  RateUnitPeriod: RateUnitPeriod | null;
  IncrementDuration: number | null;
  IncrementAmount: number | null;
  StartDuration: number | null;
  EndDuration: number | null;
}

export enum RateUnitPeriod {
  Rolling = "Rolling",
  Calendar = "Calendar",
}

export enum UnitOfTime {
  Second = "Second",
  Minute = "Minute",
  Hour = "Hour",
  Day = "Day",
  Week = "Week",
  Month = "Month",
  Year = "Year",
}

export enum Activity {
  Parking = "Parking",
  NoParking = "NoParking",
  Loading = "Loading",
  NoLoading = "NoLoading",
  Unloading = "Unloading",
  NoUnloading = "NoUnloading",
  Stopping = "Stopping",
  NoStopping = "NoStopping",
  Travel = "Travel",
  NoTravel = "NoTravel",
}

export enum UserClass {
  Bicycle = "Bicycle",
  Bus = "Bus",
  CargoBicycle = "CargoBicycle",
  Car = "Car",
  Moped = "Moped",
  Motorcycle = "Motorcycle",
  Scooter = "Scooter",
  Truck = "Truck",
  Van = "Van",

  HandicapAccessible = "Handicap-Accessible",
  Human = "Human",
  ElectricAssist = "ElectricAssist",
  Electric = "Electric",
  Combustion = "Combustion",
  Autonomous = "Autonomous",
  Purpose = "Purpose",

  Construction = "Construction",
  Delivery = "Delivery",
  EmergencyUse = "Emergency Use",
  Freight = "Freight",
  Parking = "Parking",
  Permit = "Permit",
  Rideshare = "Rideshare",
  School = "School",
  ServiceVehicles = "ServiceVehicles",
  SpecialEvents = "SpecialEvents",
  Taxi = "Taxi",
  Utilities = "utilities",
  Vending = "vending",
  WasteManagement = "WasteManagement",
  Evie = "Evie",
  Commercial = "Commercial",
}

export enum DayOfWeek {
  Monday = "Mon",
  Tuesday = "Tue",
  Wednesday = "Wed",
  Thursday = "Thu",
  Friday = "Fri",
  Saturday = "Sat",
  Sunday = "Sun",
}

export enum Month {
  January = 1,
  February = 2,
  March = 3,
  April = 4,
  May = 5,
  June = 6,
  July = 7,
  August = 8,
  September = 9,
  October = 10,
  November = 11,
  December = 12,
}

export async function getData(
  msalInstance: IPublicClientApplication,
  tokenConfig: { scopes: string[] },
  signal?: AbortSignal
) {
  let response: Response | undefined = undefined;
  const accessToken = await acquireToken(msalInstance, tokenConfig);

  try {
    response = await fetch("/api/data", {
      signal: signal,
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });
  } catch (error) {
    if ((error as Error)?.name !== "AbortError") {
      throw error;
    }

    return;
  }

  if (response.ok) {
    return await (response.json() as Promise<Zone[] | undefined>);
  } else {
    throw new Error(response.statusText);
  }
}
